import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUpRight, EyeOff, Minus, Move, RotateCcw, Square, Trash2, Type } from 'lucide-react';

/**
 * Éditeur d'annotation de la capture d'écran d'un signalement.
 *
 * Pourquoi du JSX : un canevas et des interactions au pointeur. Aucune capacité
 * reactive-json ne dessine, et la logique (formes en cours, annulation, rendu
 * composé) est algorithmique — c'est l'une des deux dérogations config-first
 * assumées du chantier.
 *
 * Le TRACÉ AU CLAVIER est hors périmètre (décision D6 du cadrage) : aucun membre
 * de l'équipe n'utilise d'outil d'assistance et l'éditeur ne s'affiche que sur
 * des postes de développement. La sémantique, elle, est faite — barre d'outils
 * étiquetée, état des boutons annoncé, Échap pour passer, annulation au clavier.
 *
 * Le rendu est TOUJOURS recomposé depuis l'image d'origine : rien n'est peint de
 * façon destructive, ce qui rend l'annulation triviale.
 *
 * Deux sorties : `onDone` remet l'image (annotée ou non) à l'appelant,
 * `onDiscard` signale qu'on part SANS capture — un signalement reste
 * possible sans image, et l'écran capturé peut montrer ce qu'on ne veut pas
 * envoyer.
 */

// Icônes plutôt que libellés : la barre tient sur une ligne et reste lisible
// même sur une capture étroite. Chaque bouton garde son nom accessible et son
// infobulle — l'icône seule ne dit rien à qui ne la reconnaît pas.
const OUTILS = [
  { id: 'rect', label: 'Rectangle', aide: 'Entourer une zone', Icone: Square },
  { id: 'arrow', label: 'Flèche', aide: 'Pointer un élément', Icone: ArrowUpRight },
  { id: 'line', label: 'Trait', aide: 'Souligner', Icone: Minus },
  { id: 'text', label: 'Texte', aide: 'Écrire sur l’image', Icone: Type },
  { id: 'mask', label: 'Masquer', aide: 'Recouvrir des données sensibles', Icone: EyeOff },
  { id: 'move', label: 'Déplacer', aide: 'Reprendre une annotation déjà posée', Icone: Move },
];

const COULEURS = ['#e11d48', '#ea580c', '#16a34a', '#2563eb', '#0f172a', '#ffffff'];
const EPAISSEUR = 3;

// Fond du masque. Neutre et sombre À DESSEIN : sa fonction est de cacher, pas de
// décorer, et il doit se lire comme « masqué » quelle que soit la couleur
// choisie pour les annotations.
const MASQUE_FOND = '#334155';
const MASQUE_MOTIF = '#94a3b8';

/**
 * Motif répété du masque : un œil barré.
 *
 * Un APLAT OPAQUE, et non un flou. Le flou ne fait que dégrader l'information —
 * on devine encore, et le résultat dépend de l'implémentation du navigateur.
 * Ici le contenu d'origine n'est tout simplement plus dans l'image exportée.
 */
function motifMasque(ctx) {
  // Tuile GÉNÉREUSE : la capture est à sa résolution native mais s'affiche
  // réduite. Une tuile serrée se lisait comme une trame indistincte au lieu du
  // pictogramme, ce qui n'aurait rien dit à qui reçoit le ticket.
  const T = 48;
  const t = document.createElement('canvas');
  t.width = T; t.height = T;
  const c = t.getContext('2d');
  c.fillStyle = MASQUE_FOND;
  c.fillRect(0, 0, T, T);
  c.strokeStyle = MASQUE_MOTIF;
  c.lineWidth = 2.2;
  c.lineCap = 'round';
  c.lineJoin = 'round';
  // Amande de l'œil.
  c.beginPath();
  c.moveTo(9, 24);
  c.quadraticCurveTo(24, 9, 39, 24);
  c.quadraticCurveTo(24, 39, 9, 24);
  c.stroke();
  // Pupille.
  c.beginPath();
  c.arc(24, 24, 5, 0, Math.PI * 2);
  c.stroke();
  // Barre.
  c.beginPath();
  c.moveTo(10, 38); c.lineTo(38, 10);
  c.stroke();
  return ctx.createPattern(t, 'repeat');
}

export default function AnnotationEditor({ blob, onDone, onDiscard }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [pret, setPret] = useState(false);
  const [outil, setOutil] = useState('rect');
  const [couleur, setCouleur] = useState(COULEURS[0]);
  const [texte, setTexte] = useState('');
  const [formes, setFormes] = useState([]);
  const [enCours, setEnCours] = useState(null);
  // Index de la forme sélectionnée (outil « Déplacer »), et point de départ du
  // glissement en cours.
  const [selection, setSelection] = useState(null);
  const glisse = useRef(null);

  // Charge la capture une fois. L'URL objet est révoquée au démontage : sans
  // cela, une capture pleine page reste en mémoire tant que l'onglet vit.
  useEffect(() => {
    if (!blob) return undefined;
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { imageRef.current = img; setPret(true); };
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  // Taille du texte proportionnelle à l'image : un corps fixe serait minuscule
  // sur une capture haute résolution.
  const corps = () => Math.max(16, Math.round((imageRef.current?.naturalWidth || 1200) / 55));

  // Boîte englobante d'une forme, dans le repère de l'image. Sert au test de
  // sélection et au liseré de la forme choisie. Le texte porte sa largeur,
  // mesurée à la pose : la remesurer ici demanderait un contexte à chaque appel.
  const boite = (f) => {
    if (f.type === 'text') {
      return { x: f.x1, y: f.y1, w: f.width || f.size * 6, h: f.size * 1.25 };
    }
    const x = Math.min(f.x1, f.x2); const y = Math.min(f.y1, f.y2);
    return { x, y, w: Math.abs(f.x2 - f.x1), h: Math.abs(f.y2 - f.y1) };
  };

  // Marge de préhension : un trait fin serait autrement impossible à attraper.
  const contient = (f, p) => {
    const b = boite(f);
    const m = 8;
    return p.x >= b.x - m && p.x <= b.x + b.w + m && p.y >= b.y - m && p.y <= b.y + b.h + m;
  };

  const dessinerForme = useCallback((ctx, f) => {
    const { x1, y1, x2, y2 } = f;
    ctx.save();
    ctx.strokeStyle = f.color;
    ctx.fillStyle = f.color;
    ctx.lineWidth = EPAISSEUR;
    ctx.lineCap = 'round';

    if (f.type === 'mask') {
      const x = Math.min(x1, x2); const y = Math.min(y1, y2);
      const w = Math.abs(x2 - x1); const h = Math.abs(y2 - y1);
      if (w > 1 && h > 1) {
        ctx.fillStyle = motifMasque(ctx);
        ctx.fillRect(x, y, w, h);
        ctx.strokeStyle = MASQUE_FOND;
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, w, h);
      }
    }
    else if (f.type === 'text') {
      // Ombre légère et courte : le texte doit rester lisible sur un fond clair
      // comme sur un fond chargé, sans devenir une vignette.
      ctx.font = `600 ${f.size}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      ctx.textBaseline = 'top';
      ctx.shadowColor = 'rgba(0,0,0,0.55)';
      ctx.shadowBlur = 3;
      ctx.shadowOffsetY = 1;
      ctx.fillText(f.text, x1, y1);
    }
    else if (f.type === 'rect') {
      ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    }
    else if (f.type === 'line') {
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    }
    else if (f.type === 'arrow') {
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const tete = Math.max(12, EPAISSEUR * 4);
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - tete * Math.cos(angle - Math.PI / 7), y2 - tete * Math.sin(angle - Math.PI / 7));
      ctx.lineTo(x2 - tete * Math.cos(angle + Math.PI / 7), y2 - tete * Math.sin(angle + Math.PI / 7));
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    [...formes, ...(enCours ? [enCours] : [])].forEach((f) => dessinerForme(ctx, f));

    // Liseré de la forme sélectionnée. Dessiné EN DERNIER pour rester visible
    // au-dessus d'un masque opaque, et en pointillés pour ne pas se confondre
    // avec un rectangle annoté.
    if (selection != null && formes[selection]) {
      const b = boite(formes[selection]);
      ctx.save();
      ctx.setLineDash([8, 6]);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#0ea5e9';
      ctx.strokeRect(b.x - 6, b.y - 6, b.w + 12, b.h + 12);
      ctx.restore();
    }
  }, [pret, formes, enCours, selection, dessinerForme]);

  // Le canevas est affiché à une taille réduite : on ramène les coordonnées du
  // pointeur dans le repère de l'IMAGE, sans quoi les tracés seraient décalés.
  const point = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return {
      x: (e.clientX - r.left) * (canvasRef.current.width / r.width),
      y: (e.clientY - r.top) * (canvasRef.current.height / r.height),
    };
  };

  const onPointerDown = (e) => {
    const p = point(e);

    // Déplacement : on attrape la forme la plus HAUTE sous le pointeur, donc la
    // dernière dessinée — c'est celle que l'on voit.
    if (outil === 'move') {
      const i = [...formes].reverse().findIndex((f) => contient(f, p));
      const idx = i === -1 ? null : formes.length - 1 - i;
      setSelection(idx);
      glisse.current = idx == null ? null : { x: p.x, y: p.y };
      if (idx != null) e.currentTarget.setPointerCapture(e.pointerId);
      return;
    }

    // Le texte se POSE d'un clic, il ne se trace pas : on saisit d'abord dans la
    // barre d'outils, puis on choisit l'endroit.
    if (outil === 'text') {
      if (!texte.trim()) return;
      const taille = corps();
      const mesure = canvasRef.current.getContext('2d');
      mesure.save();
      mesure.font = `600 ${taille}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;
      const width = mesure.measureText(texte.trim()).width;
      mesure.restore();
      // x2/y2 suivent x1/y1 pour que le déplacement traite le texte comme les
      // autres formes, sans cas particulier.
      setFormes((prev) => [...prev, {
        type: 'text', x1: p.x, y1: p.y, x2: p.x, y2: p.y,
        text: texte.trim(), color: couleur, size: taille, width,
      }]);
      return;
    }
    e.currentTarget.setPointerCapture(e.pointerId);
    setEnCours({ type: outil, x1: p.x, y1: p.y, x2: p.x, y2: p.y, color: couleur });
  };
  const onPointerMove = (e) => {
    if (glisse.current && selection != null) {
      const p = point(e);
      const dx = p.x - glisse.current.x; const dy = p.y - glisse.current.y;
      glisse.current = { x: p.x, y: p.y };
      setFormes((prev) => prev.map((f, i) => (i === selection
        ? { ...f, x1: f.x1 + dx, y1: f.y1 + dy, x2: f.x2 + dx, y2: f.y2 + dy }
        : f)));
      return;
    }
    if (!enCours) return;
    const p = point(e);
    setEnCours((f) => ({ ...f, x2: p.x, y2: p.y }));
  };
  const onPointerUp = () => {
    glisse.current = null;
    if (!enCours) return;
    // Un simple clic sans déplacement ne laisse rien : évite les artefacts.
    const bouge = Math.hypot(enCours.x2 - enCours.x1, enCours.y2 - enCours.y1) > 4;
    if (bouge) setFormes((prev) => [...prev, enCours]);
    setEnCours(null);
  };

  const valider = () => {
    canvasRef.current.toBlob((b) => onDone(b || blob), 'image/png');
  };

  useEffect(() => {
    const onKey = (e) => {
      // Ne pas voler les touches à la saisie de texte.
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'Escape') onDiscard();
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); setFormes((p) => p.slice(0, -1)); setSelection(null); }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        setSelection((sel) => {
          if (sel == null) return sel;
          e.preventDefault();
          setFormes((p) => p.filter((_, i) => i !== sel));
          return null;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDiscard]);

  return (
    <div className="flex h-full flex-col gap-3">
      {/* `pr-10` : la croix de fermeture de la popin est posée en absolu dans le
          coin haut-droit du panneau (ReportOverlay), au-dessus de cette barre.
          Sans cette réserve, elle chevauche « Continuer », le dernier bouton du
          groupe aligné à droite — deux cibles cliquables superposées. */}
      <div className="flex flex-wrap items-center gap-2 pr-10">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Annoter</span>
        <div className="flex gap-1" role="group" aria-label="Outils d'annotation">
          {OUTILS.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { setOutil(o.id); if (o.id !== 'move') setSelection(null); }}
              aria-pressed={outil === o.id}
              aria-label={`${o.label} — ${o.aide}`}
              title={`${o.label} — ${o.aide}`}
              className={`btn btn-sm ${outil === o.id ? 'btn-primary' : 'btn-secondary'}`}
            >
              <o.Icone size={16} aria-hidden="true" />
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1" role="group" aria-label="Couleur des annotations">
          {COULEURS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCouleur(c)}
              aria-label={`Couleur ${c}`}
              aria-pressed={couleur === c}
              style={{ backgroundColor: c }}
              className={`h-6 w-6 rounded-full border ${couleur === c ? 'border-slate-900 ring-2 ring-slate-400' : 'border-slate-300'}`}
            />
          ))}
          <input
            type="color"
            value={couleur}
            onChange={(e) => setCouleur(e.target.value)}
            aria-label="Choisir une autre couleur"
            className="h-6 w-8 cursor-pointer rounded border border-slate-300 bg-white p-0"
          />
        </div>

        <button
          type="button"
          onClick={() => { setFormes((p) => p.filter((_, i) => i !== selection)); setSelection(null); }}
          disabled={selection == null}
          aria-label="Supprimer l’annotation sélectionnée"
          title="Supprimer l’annotation sélectionnée"
          className="btn btn-secondary btn-sm"
        >
          <Trash2 size={16} aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={() => { setFormes([]); setSelection(null); }}
          disabled={formes.length === 0}
          aria-label="Recommencer — effacer toutes les annotations"
          title="Recommencer — effacer toutes les annotations"
          className="btn btn-secondary btn-sm"
        >
          <RotateCcw size={16} aria-hidden="true" />
        </button>
        <div className="ml-auto flex gap-2">
          {/* Signaler SANS image : la capture est utile, pas obligatoire, et
              parfois elle montre ce qu'on ne veut pas envoyer. */}
          <button type="button" onClick={onDiscard} className="btn btn-secondary btn-sm">
            Sans capture
          </button>
          <button type="button" onClick={valider} className="btn btn-primary btn-sm">Continuer</button>
        </div>
      </div>

      {outil === 'text' && (
        <div className="flex items-center gap-2">
          <label htmlFor="annot-texte" className="text-xs text-slate-600">Texte à poser</label>
          <input
            id="annot-texte"
            type="text"
            value={texte}
            onChange={(e) => setTexte(e.target.value)}
            placeholder="Saisissez, puis cliquez sur l’image"
            className="input flex-1"
          />
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-auto rounded border border-slate-200 bg-slate-100 p-2">
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          className={`mx-auto block max-w-full touch-none ${outil === 'move' ? 'cursor-move' : 'cursor-crosshair'}`}
          aria-label="Capture d'écran de la page signalée, à annoter à la souris"
        />
      </div>
    </div>
  );
}
